/**
 * Maps an array of IDs to human-readable names using a lookup array.
 * Generic utility that works with any ID type (string or number).
 *
 * @param ids - Array of IDs to map
 * @param items - Lookup array with id and name properties
 * @param fallback - Optional fallback function when ID is not found (defaults to String(id))
 * @returns Array of names, with fallback to ID string if name not found
 */

type LookupItem = { id: string | number; name: string };

const nameMapCache = new WeakMap<
  LookupItem[] | ReadonlyArray<LookupItem>,
  Map<string | number, string>
>();

function getNameMap(
  items: ReadonlyArray<LookupItem>
): Map<string | number, string> {
  const cached = nameMapCache.get(items);
  if (cached) return cached;

  const map = new Map(items.map((i) => [i.id, i.name] as const));
  // WeakMap keys must be objects; arrays are objects, ReadonlyArray is still an array at runtime.
  nameMapCache.set(items, map);
  return map;
}

export function mapIdsToNames<T extends string | number>(
  ids: T[],
  items: Array<{ id: string | number; name: string }>,
  fallback?: (id: T) => string
): string[] {
  if (!ids?.length) return [];
  if (!items?.length) return ids.map((id) => fallback?.(id) ?? String(id));

  const nameMap = getNameMap(items);
  return ids.map((id) => {
    const direct = nameMap.get(id);
    if (direct) return direct;

    // Be tolerant to string/number mismatches coming from external sources.
    if (typeof id === "number") {
      const asString = nameMap.get(String(id));
      if (asString) return asString;
    } else {
      const asNumber = Number(id);
      if (Number.isFinite(asNumber)) {
        const fromNumber = nameMap.get(asNumber);
        if (fromNumber) return fromNumber;
      }
    }

    return fallback?.(id) ?? String(id);
  });
}
